// Lobby logic for the (simulated) online mode: game sizes, maps, an expanded
// color palette, bot-name generation, and building a real match config of
// 1–2 local humans + bots filling the remaining slots.

import * as gsf from "core/src/core/game-state-functions.js";
import { playstyleDisabledPowerUps, playstyleSpawnChance } from "core/src/playstyles.js";
import { LOAD_TESTS, isLoadTestBase } from "core/src/ai/load-tests.js";
import { crowdLevelNumber } from "core/src/ai/crowd.js";
import { arenaValue, sideOfKey } from "core/src/core/arena-sizes.js";
import { arenaLabel } from "../setup/maps";
import { keyPairs } from "../../shared/key-util.js";
import { WORM_COLOR_IDS } from "../../shared/worm-colors";
import * as scoreUtil from "core/src/core/score/score-util.js";

// The expanded color palette now lives in one place (shared/worm-colors.ts),
// which registers all the extra colors onto core's shared map. Past the palette
// size, colors simply repeat.
export const COLOR_IDS: string[] = WORM_COLOR_IDS.slice();

// --- Maps -------------------------------------------------------------------
export type MapDef = { value: string; label: string };

const M = {
  smallCircle: { value: "Circle 520", label: "Small circle" },
  bigCircle: { value: "Circle 820", label: "Big circle" },
  smallRect: { value: "Rectangle 640 460", label: "Small rectangle" },
  bigRect: { value: "Rectangle 920 640", label: "Big rectangle" },
  smallSquare: { value: "Square 520", label: "Small square" },
  bigSquare: { value: "Square 820", label: "Big square" },
};

// Convert a server map descriptor (from lobby_counts) into a MapDef so the card
// preview/label render the exact map the box will play this cycle.
export function descToMapDef(desc: any): MapDef | null {
  if (!desc) return null;
  if (desc.type === "Square") return { value: "Square " + desc.size, label: "Square" };
  if (desc.type === "Circle") return { value: "Circle " + desc.radius * 2, label: "Circle" };
  if (desc.type === "Rectangle") return { value: "Rectangle " + desc.width + " " + desc.height, label: "Rectangle" };
  if (desc.type === "Stadium") return { value: "Stadium " + desc.width + " " + desc.height, label: "Stadium" };
  return null;
}

// "Square 700" / "Circle 700" / "Rectangle 990 495" / "Stadium 929 464" -> a core map.
export function createMap(mapString: string): any {
  return gsf.createMapFromValue(mapString);
}

// A standard arena of a box's size: the landscape 2:1 rectangle with the area of
// that square (core equalAreaRectangle). The server's boxes roll it or the 2:1
// stadium of that size each cycle; the Lobby's counts carry the rolled one.
const rectMap = (side: number, label: string): MapDef => ({ value: arenaValue("rectangle", side), label });

// --- Game sizes -------------------------------------------------------------
// "stress…" are the hidden load-test boxes (/game/stress, /game/stress24, ...
// admins only): one per crowd level at that level's biggest roster (core
// load-tests.js), never listed in the lobby.
export type StressSize = `stress${string}`;
export type GameSize = "small" | "medium" | "large" | StressSize;
export const isStressSize = (size: string | null | undefined): size is StressSize => !!size && isLoadTestBase(size);
// One entry per load-test box, for the Records below.
function perLoadTest<T>(make: (t: { base: string; level: number; capacity: number; mapSize: number }) => T): Record<StressSize, T> {
  return Object.fromEntries(LOAD_TESTS.map((t: any) => [t.base, make(t)])) as Record<StressSize, T>;
}
const stressMap = (size: number): MapDef => rectMap(size, arenaLabel(arenaValue("rectangle", size)));

interface SizeDef {
  title: string;
  blurb: string;
  capacities: number[];
  maps: MapDef[];
}

export const SIZES: Record<GameSize, SizeDef> = {
  small: {
    title: "Duel",
    blurb: "Quick & tight",
    capacities: [2, 3, 4],
    maps: [M.smallCircle, M.smallSquare],
  },
  medium: {
    title: "Brawl",
    blurb: "Battle it out",
    capacities: [6, 8, 10],
    maps: [M.smallCircle, M.bigCircle, M.smallSquare, M.bigSquare],
  },
  large: {
    title: "Royale Chaos",
    blurb: "Good luck",
    capacities: [15, 20, 25],
    maps: [M.bigSquare, M.bigCircle],
  },
  ...perLoadTest((t) => ({
    title: "Load test · level " + crowdLevelNumber(t.level),
    blurb: t.capacity === 2 ? "You and 1 other" : "You and " + (t.capacity - 1) + " others",
    capacities: [t.capacity],
    maps: [stressMap(t.mapSize)],
  })),
};

export const COUNTDOWN_SECONDS = 120;

// The REAL server matchmaking boxes (mirror game-server's PUBLIC_ROOMS). The card
// preview, labels and capacity must use these so what you see on the box is the
// map/size actually played once the match starts (previously the card picked a
// random map, e.g. a circle, while the server always plays a square). Each cycle the
// server rolls the box's rectangle OR stadium; the cards and the box's page show
// the rolled map from the Lobby's counts (descToMapDef), so PUBLIC_MAPS is only the
// placeholder until those arrive.
export const PUBLIC_CAPS: Record<GameSize, number> = { small: 5, medium: 12, large: 30, ...perLoadTest((t) => t.capacity) };
export const PUBLIC_MAPS: Record<GameSize, MapDef> = {
  small: rectMap(sideOfKey("medium"), "Medium Rectangle"),
  medium: rectMap(sideOfKey("large"), "Large Rectangle"),
  large: rectMap(sideOfKey("huge"), "Huge Rectangle"),
  ...perLoadTest((t) => stressMap(t.mapSize)),
};

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export interface LobbyGame {
  size: GameSize;
  title: string;
  blurb: string;
  capacity: number;
  map: MapDef;
  count: number; // simulated players currently in the lobby
  secondsLeft: number;
  roster: string[]; // pre-shuffled bot names, revealed as the lobby fills
}

// URL slugs for the matchmaking boxes: /game/duel|brawl|royale. The slug is the
// player-facing identity of a box; the size is the server's base room key
// ("public:<size>"). Giving each box a real URL is what lets a browser reload
// land back in the same session (see OnlineMatchPage + the sticky room in net.ts).
export const BOX_SLUGS: Record<GameSize, string> = { small: "duel", medium: "brawl", large: "royale", ...perLoadTest((t) => t.base) };
export const SIZE_BY_SLUG: Record<string, GameSize> = { duel: "small", brawl: "medium", royale: "large", ...perLoadTest((t) => t.base as StressSize) };

// mm:ss for the boxes' countdowns.
export function fmtClock(s: number): string {
  const sec = Math.max(0, s);
  const m = Math.floor(sec / 60);
  return m + ":" + String(sec % 60).padStart(2, "0");
}

export function makeGame(size: GameSize): LobbyGame {
  const def = SIZES[size];
  // Capacity + map mirror the server so the card preview matches the real match.
  const capacity = PUBLIC_CAPS[size];
  return {
    size,
    title: def.title,
    blurb: def.blurb,
    capacity,
    map: PUBLIC_MAPS[size],
    count: 1 + Math.floor(Math.random() * Math.max(1, Math.floor(capacity / 3))),
    secondsLeft: COUNTDOWN_SECONDS,
    roster: shuffledNames(capacity),
  };
}

// --- Bot names --------------------------------------------------------------
// A pool of ~100 handle-style names. A game's roster is a shuffled slice of
// these, revealed one by one as the (simulated) lobby fills with bots.
export const BOT_NAMES = [
  "SneakyViper", "TurboNoodle", "WigglyWill", "CurveBoss", "Slytherin", "DashPanda", "NeonNarwhal", "ZigZagZoe",
  "Coily", "MadDasher", "GapHunter", "TrailMix", "PivotPete", "Wormtongue", "Loopy", "SilentSnek",
  "BlazeRunner", "Quarkk", "WispyWanda", "IronCoil", "FrostByte", "SolarFlare", "HyperHank", "ZenMaster",
  "CrispyTaco", "Slinky", "VividVee", "ShadowStep", "LuckyLuke", "BraveBert", "RustyGears", "CosmicCarl",
  "Noodler", "TheWall", "GapGremlin", "SpeedyG", "TurnLeftPls", "Snekzilla", "Bendy", "Squiggle",
  "VenomTrail", "PixelPit", "Glidey", "BoltFromBlue", "CometKid", "LynxLuna", "HawkEye99", "DriftKing",
  "Phantomz", "Mr_Curve", "CurveBall", "Twisty", "Spaghetto", "RamenLord", "UdonU", "PastaLaVista",
  "WallTapper", "GhostGap", "QuickTurn", "HardLeft", "OvR_Steer", "Spinout", "Tangler", "KnotAgain",
  "SnekInTheGrass", "Viperion", "NoScopeNoodle", "Trailblaze", "CurvyMcCurve", "Slip_n_Slide", "EdgeLord", "WrapAround",
  "DodgeThis", "GapGod", "TightTurner", "Driftwood", "PivotPro", "Coilette", "Slidey", "Zoomies",
  "NeonGhost", "PinkPivot", "CyanSlayer", "GoldRush", "VioletVortex", "MintCondition", "CoralCrash", "SkyHigh",
  "RogueWorm", "ApexCurve", "LastGapLarry", "ClutchCurve", "BotMcBotface", "BeepBoop", "01100010", "NPC_Steve",
  "Autopilot", "Deadline", "WallHugger", "FreeSpirit",
];

// A random fun handle for a local human's default name (reuses the bot pool so
// players blend in with the "online" crowd). Avoids any names in `exclude`.
export function randomPlayerName(exclude: string[] = []): string {
  const pool = BOT_NAMES.filter((n) => exclude.indexOf(n) === -1);
  return pool[Math.floor(Math.random() * pool.length)] || "Player";
}

// Fisher-Yates shuffle (partial) for a distinct slice of `count` names.
export function shuffledNames(count: number): string[] {
  const pool = BOT_NAMES.slice();
  const n = Math.min(count, pool.length);
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (pool.length - i));
    const t = pool[i];
    pool[i] = pool[j];
    pool[j] = t;
  }
  const out = pool.slice(0, count);
  // If a game ever needs more than the pool size, pad with numbered bots.
  let k = 1;
  while (out.length < count) out.push("Bot " + k++);
  return out;
}

// Colors for the filling bots: human-free colors first, then cycle the palette.
// Shared by the lobby chips and the real match so a bot's color is consistent.
export function botColorPalette(humanColorIds: string[]): string[] {
  const used = new Set(humanColorIds);
  return COLOR_IDS.filter((c) => !used.has(c)).concat(COLOR_IDS);
}

// --- Live card previews -----------------------------------------------------
// A looping all-bot game used as the animated background of each lobby card.
// Runs the game's real player count. Cached per (map + worms) so the running
// preview isn't recreated on every re-render.
// NOTE: full counts (up to 40) are intentionally heavy; a future optimization is
// to swap these live games for pre-recorded replays.
const previewCache: Record<string, any> = {};

export function previewConfig(map: MapDef, worms: number, playstyle?: string | null): any {
  const key = map.value + "#" + worms + "#" + (playstyle || "");
  if (!previewCache[key]) {
    const players = [];
    for (let i = 0; i < worms; i++) {
      players.push({
        type: "bot",
        colorId: COLOR_IDS[i % COLOR_IDS.length],
        name: "P" + i,
        left: null,
        right: null,
        id: "pv" + i,
      });
    }
    const cfg: any = { players, map: createMap(map.value), maxScore: 999 };
    // Match the box's playstyle so the decorative preview spawns that flavor's
    // power-ups at its spawn rate (undefined playstyle = the engine defaults).
    if (playstyle) {
      cfg.disabledPowerUps = playstyleDisabledPowerUps(playstyle);
      cfg.powerUpSpawnChance = playstyleSpawnChance(playstyle);
    }
    previewCache[key] = cfg;
  }
  return previewCache[key];
}

// --- Build a match config ---------------------------------------------------
// Humans arrive already configured (color, name, key bindings) from the lobby's
// player setup; bots fill the rest, preferring colors the humans didn't take.
export interface HumanInput {
  colorId: string;
  name: string;
  left: string | null;
  right: string | null;
  flag?: string | null;
  victoryAnim?: string | null;
  headStyle?: string | null;
}

export function buildMatchConfig(game: LobbyGame, humans: HumanInput[]): any {
  const players: any[] = [];
  const usedColors = new Set<string>();

  humans.forEach((h, i) => {
    const name = (h.name || "Player " + (i + 1)).slice(0, 16);
    usedColors.add(h.colorId);
    players.push({
      type: "human",
      colorId: h.colorId,
      name,
      left: h.left,
      right: h.right,
      flag: h.flag ?? null,
      victoryAnim: h.victoryAnim ?? null,
      headStyle: h.headStyle ?? null,
      id: "h" + i,
    });
  });

  // Bots fill the rest using the game's roster names (same ones shown as chips
  // in the lobby) and human-free colors first.
  const palette = botColorPalette([...usedColors]);
  const botCount = Math.max(0, game.capacity - players.length);
  for (let i = 0; i < botCount; i++) {
    players.push({
      type: "bot",
      colorId: palette[i % palette.length],
      name: game.roster[i] || "Bot " + (i + 1),
      left: null,
      right: null,
      // no cosmetics offline: those are for the online personas (core's
      // fill-cosmetics roll on the server)
      victoryAnim: null,
      headStyle: null,
      id: "b" + i,
    });
  }

  // Score target scales with lobby size so big games last a few rounds.
  const maxScore = scoreUtil.targetScoreFor(game.capacity);
  return { players, map: createMap(game.map.value), maxScore };
}

// Short, shareable, unambiguous join code (no 0/O/1/I to avoid mis-typing).
// Used by the Host Game button and the Join page's "create a lobby" shortcut.
export function genLobbyCode(): string {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 4; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}
